import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Col, Form, notification, Row } from 'antd'
import { Formik } from 'formik'
import { RouteComponentProps } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { Card, PageHeader } from 'src/components/Ant'
import { Input } from 'src/components/Formik'
import { Policy, PolicyRule, Reason } from 'src/types'
import * as PoliciesService from 'src/services/api/policies'

import { Content, Spin } from './styles'
import { PolicyRules } from './policy-rules'
import { sanitizeToPost } from './sanitize-to-post'
import * as InitialValue from './initial-values'
import Limits from './limits/'
import ModalLinkReason from './modal-link-reason'
import validationSchema from './validation-schema'

type Params = {
  policy_id?: string
}

export default function PolicyForm({
  history,
  match: {
    params: { policy_id }
  }
}: RouteComponentProps<Params>) {
  const {
    me: {
      organization: { organization_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef<Formik<Policy>>()

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalReasonVisible, setModalReasonVisible] = useState(false)

  function toggle() {
    setModalReasonVisible(v => !v)
  }

  const { t } = useTranslation()

  async function onSubmit(values: Policy) {
    setSaving(true)

    const sanitizedValues = sanitizeToPost(values)
    const response = await PoliciesService.save({
      organization_id,
      policy: {
        ...sanitizedValues,
        organization_id
      }
    })

    setSaving(false)
    if (response.status === 200) {
      notification.success({
        duration: 10,
        message: t('common.edit-done-successfully'),
        placement: 'bottomRight'
      })
      history.push('/policies')
      return
    }

    //@ts-ignore
    const message = response.data?.reasons?.message || response.data?.reasons
    if (typeof message === 'string') {
      notification.error({
        duration: 10,
        message: message,
        placement: 'bottomRight'
      })
      return
    }

    notification.error({
      duration: 10,
      message: t('common.response.status-500'),
      placement: 'bottomRight'
    })
  }

  const getPolicy = useCallback(async () => {
    setLoading(true)

    const response = await PoliciesService.getById({
      organization_id,
      policy_id
    })

    setLoading(false)

    if (response.data) {
      refFormik.current.resetForm(response.data)
    }
  }, [policy_id, organization_id])

  useEffect(() => {
    if (policy_id) {
      getPolicy()
    }
  }, [policy_id, getPolicy])

  if (loading) {
    return <Spin />
  }

  const initialValues = InitialValue.getInitialValuesPolicy({
    organization_id,
    policy_id
  })
  const initialValuesPolicyRules = InitialValue.getInitialValuesPolicyRules({
    policy_id
  })
  function modalReasonHandleSubmit(values: {
    reason_id: string
    reason: Reason
  }) {
    if (!refFormik.current || !values) {
      notification.warning({
        duration: 10,
        message: t('web.policies.rules.feedback.reason-not-selected'),
        placement: 'bottomRight'
      })
      toggle()
      return
    }

    const policy_rules = refFormik.current.getFormikBag().values
      ?.policy_rules as PolicyRule[]
    const foundIndex = policy_rules?.findIndex(
      item => +item.reason_id === +values.reason_id
    )

    if (foundIndex > -1) {
      notification.warning({
        duration: 10,
        message: t('web.policies.rules.feedback.reason-has-linked'),
        placement: 'bottomRight'
      })
      return
    }

    refFormik.current.getFormikActions().setFieldValue('policy_rules', [
      {
        ...initialValuesPolicyRules,
        reason_id: values.reason_id,
        reason: values.reason
      },
      ...policy_rules
    ])
    toggle()
  }

  return (
    <>
      <PageHeader
        extra={[
          <Button
            disabled={saving}
            key="cancel"
            loading={saving}
            onClick={() => history.goBack()}
            size="default"
            type="default"
          >
            {t('common.actions.cancel')}
          </Button>,
          <Button
            disabled={saving}
            htmlType="submit"
            key="save"
            loading={saving}
            onClick={() => refFormik.current.submitForm()}
            size="default"
            type="primary"
          >
            {t('common.actions.save')}
          </Button>
        ]}
        title="web.policies.title-singular"
      />
      <Content>
        <Card>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            ref={refFormik}
            render={formikProps => (
              <Form onSubmit={formikProps.handleSubmit}>
                <Row gutter={24}>
                  <Col offset={1} span={10}>
                    <Input
                      formikProps={formikProps}
                      label="web.setup.form.field-title.title"
                      name="name"
                      placeholder="web.setup.form.field-title.placeholder"
                    />
                  </Col>
                  <Col span={12}>
                    <Input
                      formikProps={formikProps}
                      label="web.policies.rules.description"
                      name="description"
                      placeholder="web.policies.rules.description-placeholder"
                    />
                  </Col>
                </Row>
                <PolicyRules formikProps={formikProps} toggle={toggle} />
                <Limits formikProps={formikProps} />
              </Form>
            )}
            validationSchema={validationSchema}
          />
        </Card>
      </Content>
      <ModalLinkReason
        onSubmit={modalReasonHandleSubmit}
        toggle={toggle}
        visible={modalReasonVisible}
      />
    </>
  )
}
